"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useCartStore } from "@/lib/cart-store";

type AddToCartButtonProps = {
  product: {
    id: string;
    slug: string;
    name: string;
    price: number;
    image?: string | null;
    isGlitter?: boolean;
    categoryName?: string;
    stock: number;
  };
};

export function AddToCartButton({ product }: AddToCartButtonProps) {
  const addItem = useCartStore((state) => state.addItem);
  const router = useRouter();
  const { status } = useSession();
  const [added, setAdded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [burst, setBurst] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleAdd = async () => {
    if (product.stock <= 0 || isSubmitting) return;

    setIsSubmitting(true);

    addItem({
      id: product.id,
      slug: product.slug,
      name: product.name,
      price: product.price,
      image: product.image ?? null,
      isGlitter: product.isGlitter,
      categoryName: product.categoryName,
    });

    if (status === "authenticated") {
      try {
        const res = await fetch("/api/cart/sync", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            productId: product.id,
            quantity: 1,
          }),
        });

        if (res.ok) {
          router.refresh();
        }
      } catch (error) {
        console.error("Erreur de synchronisation du panier :", error);
      }
    }

    setBurst(true);
    setAdded(true);
    setIsSubmitting(false);

    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = window.setTimeout(() => {
      setAdded(false);
      setBurst(false);
    }, 1800);
  };

  return (
    <div className="relative inline-flex">
      {burst ? (
        <span className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-[var(--primary)]/20 blur-xl animate-pulse" />
      ) : null}

      <button
        onClick={handleAdd}
        disabled={product.stock <= 0 || isSubmitting}
        className={[
          "group relative inline-flex min-w-[210px] items-center justify-center overflow-hidden rounded-full px-6 py-3 text-sm font-medium text-white shadow-[0_14px_34px_rgba(255,63,142,0.18)] transition-all duration-300",
          product.stock <= 0
            ? "cursor-not-allowed bg-[var(--foreground)]/35 opacity-60"
            : added
            ? "scale-[1.02] bg-emerald-600"
            : "bg-[var(--primary)] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(255,63,142,0.26)] active:translate-y-0",
        ].join(" ")}
      >
        <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0.18)_35%,transparent_70%)] opacity-0 transition duration-500 group-hover:translate-x-full group-hover:opacity-100" />

        <span className="relative flex items-center gap-2">
          {product.stock <= 0 ? (
            <>
              <span className="h-2 w-2 rounded-full bg-white/80" />
              <span>Indisponible</span>
            </>
          ) : isSubmitting ? (
            <>
              <span className="h-4 w-4 rounded-full border-2 border-white/45 border-t-white animate-spin" />
              <span>Ajout en cours...</span>
            </>
          ) : added ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4.5 w-4.5"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span>Ajouté au panier</span>
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4.5 w-4.5 transition duration-300 group-hover:scale-110"
              >
                <circle cx="8" cy="21" r="1" />
                <circle cx="19" cy="21" r="1" />
                <path d="M2.5 3H5l2.3 10.2a1 1 0 0 0 1 .8h9.7a1 1 0 0 0 1-.8L21 6H6.2" />
              </svg>
              <span>Ajouter au panier</span>
            </>
          )}
        </span>
      </button>
    </div>
  );
}